// Newsroom dashboard — the whole point of the AI reporter/editor/curator
// stack. Everything staff needs to run the newsroom without a redeploy.
//
// Three tabs:
//   1. Kanban  — story lifecycle board; click a card for detail drawer
//   2. Tuning  — pick an agent, edit prompts / thresholds / feeds
//   3. Tip in  — drop a URL for a Reporter to investigate
//
// All state lives in DB; every change round-trips through /api/admin/newsroom
// (mounted with staff middleware).

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';

type StoryStatus = 'scouting'|'investigating'|'ready'|'published'|'monitoring'|'killed';
type Confidence  = 'unknown'|'unconfirmed'|'reported'|'confirmed'|'contradicted'|'gap';

interface Story {
    id: string;
    community_id: string;
    reporter_id?: string;
    editor_id?: string;
    slug: string;
    headline: string;
    status: StoryStatus;
    confidence: Confidence;
    kill_reason?: string;
    editor_hold_note?: string;
    published_post_id?: string;
    first_seen_at: string;
    updated_at: string;
}
interface Fact {
    id: string; story_id: string; claim: string; dimension?: string;
    source_url?: string; source_kind?: string; source_quote?: string;
    confidence: Confidence; created_at: string;
}
interface Outreach {
    id: string; story_id: string; channel: string; recipient: string;
    contact?: string; question: string; status: string;
    sent_at?: string; responded_at?: string; response?: string;
    created_at: string;
}
interface Version {
    id: string; story_id: string; post_id?: string; version_num: number;
    headline: string; body: string; what_changed?: string; published_at: string;
}
interface Agent {
    id: string; name: string; community_id: string;
    community_slug?: string; role: string; model: string;
    approve_style: string; active: boolean;
}
// Raw shape returned by GET /api/admin/ai-agents — Go's default json tags
// leak PascalCase from the aieditor.Agent struct.
interface RawAgent {
    id: string; Name: string; CommunityID: string; CommunitySlug?: string;
    Role?: string; Model: string; ApproveStyle: string; Active: boolean;
}
interface Settings {
    min_facts_to_publish: number;
    min_sources_to_publish: number;
    min_confidence_to_publish: string;
    require_outreach_before_publish: boolean;
    max_speculation_ratio: number;
    auto_kill_after_days: number;
    personality: string;
    relevance_profile: string;
    triage_model: string;
    triage_max_tokens: number;
}
interface Feed {
    id: string; agent_id: string; kind: string; label: string;
    url?: string; query?: string; poll_interval_s: number; active: boolean;
    last_polled_at?: string; last_error?: string;
}

const COLUMNS: { status: StoryStatus; title: string }[] = [
    { status: 'scouting',      title: 'Scouting' },
    { status: 'investigating', title: 'Investigating' },
    { status: 'ready',         title: 'Ready to publish' },
    { status: 'published',     title: 'Published' },
    { status: 'monitoring',    title: 'Monitoring' },
    { status: 'killed',        title: 'Killed' },
];

export function AdminNewsroom() {
    const [tab, setTab] = useState<'kanban'|'tuning'|'tip'>('kanban');
    return (
        <main style={{ padding: '20px 24px 60px', maxWidth: 1400, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontFamily: 'Newsreader,serif', fontSize: 28 }}>Newsroom</h1>
            </div>
            <nav style={adminNav}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>
            <nav style={{ display: 'flex', gap: 16, borderBottom: '1px solid #ccc', marginBottom: 20 }}>
                {(['kanban','tuning','tip'] as const).map(t => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        style={{
                            background: 'none', border: 0, padding: '8px 4px', cursor: 'pointer',
                            fontFamily: '-apple-system,sans-serif', fontSize: 13,
                            textTransform: 'uppercase', letterSpacing: 2,
                            color: tab === t ? '#111' : '#888',
                            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                        }}
                    >{t === 'kanban' ? 'Board' : t === 'tuning' ? 'Tuning' : 'Tip in'}</button>
                ))}
            </nav>
            {tab === 'kanban' && <KanbanBoard />}
            {tab === 'tuning' && <TuningPanel />}
            {tab === 'tip'    && <TipForm />}
        </main>
    );
}

// ---------------------------------------------------------------- Kanban

function KanbanBoard() {
    const api = useApi();
    const [stories, setStories] = useState<Story[]>([]);
    const [selected, setSelected] = useState<Story | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get<{ stories: Story[] }>('/api/admin/newsroom/stories?limit=200');
            setStories(r.stories ?? []);
        } catch (e) {
            console.error('newsroom stories load', e);
        }
        setLoading(false);
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const move = useCallback(async (story: Story, status: StoryStatus) => {
        const note = status === 'killed' ? window.prompt('Kill reason?') || '' :
                     status === 'investigating' && story.status === 'ready' ? window.prompt('Push-back note?') || '' : '';
        try {
            await api.patch(`/api/admin/newsroom/stories/${story.id}/status`, { status, note });
            await load();
        } catch (e) {
            alert('Move failed: ' + (e as Error).message);
        }
    }, [api, load]);

    return (
        <>
            <div style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="button" onClick={load} style={btnSm}>{loading ? '…' : 'Refresh'}</button>
                <span style={{ fontSize: 11, color: '#888' }}>{stories.length} stories</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(180px, 1fr))`, gap: 12 }}>
                {COLUMNS.map(col => {
                    const rows = stories.filter(s => s.status === col.status);
                    return (
                        <div key={col.status} style={colStyle}>
                            <div style={colHead}>{col.title} <span style={{ color: '#999' }}>· {rows.length}</span></div>
                            {rows.map(s => <StoryCard key={s.id} story={s} onOpen={() => setSelected(s)} onMove={m => move(s, m)} />)}
                            {rows.length === 0 && <div style={{ padding: 8, color: '#aaa', fontSize: 11, fontStyle: 'italic' }}>—</div>}
                        </div>
                    );
                })}
            </div>
            {selected && <StoryDrawer story={selected} onClose={() => { setSelected(null); load(); }} />}
        </>
    );
}

function StoryCard({ story, onOpen, onMove }: { story: Story; onOpen: () => void; onMove: (s: StoryStatus) => void }) {
    const nextByStatus: Partial<Record<StoryStatus, StoryStatus[]>> = {
        scouting:      ['investigating', 'killed'],
        investigating: ['ready', 'killed'],
        ready:         ['published', 'investigating'],
        published:     ['monitoring', 'killed'],
        monitoring:    ['published', 'killed'],
        killed:        ['scouting'],
    };
    const nexts = nextByStatus[story.status] ?? [];
    return (
        <div style={cardStyle}>
            <button type="button" onClick={onOpen} style={{ ...cardTitle, textAlign: 'left', background: 'none', border: 0, padding: 0, cursor: 'pointer', width: '100%' }}>
                {story.headline}
            </button>
            <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                {story.confidence} · {new Date(story.updated_at).toLocaleDateString()}
            </div>
            {(story.kill_reason || story.editor_hold_note) && (
                <div style={{ fontSize: 10, color: '#8b1919', marginTop: 4, fontStyle: 'italic' }}>
                    {story.kill_reason || story.editor_hold_note}
                </div>
            )}
            <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                {nexts.map(n => (
                    <button key={n} type="button" onClick={() => onMove(n)} style={moveBtn}>→ {n}</button>
                ))}
            </div>
        </div>
    );
}

function StoryDrawer({ story, onClose }: { story: Story; onClose: () => void }) {
    const api = useApi();
    const [facts, setFacts]         = useState<Fact[]>([]);
    const [outreach, setOutreach]   = useState<Outreach[]>([]);
    const [versions, setVersions]   = useState<Version[]>([]);

    const load = useCallback(async () => {
        try {
            const [f, o, v] = await Promise.all([
                api.get<{ facts: Fact[] }>(`/api/admin/newsroom/stories/${story.id}/facts`),
                api.get<{ outreach: Outreach[] }>(`/api/admin/newsroom/stories/${story.id}/outreach`),
                api.get<{ versions: Version[] }>(`/api/admin/newsroom/stories/${story.id}/versions`),
            ]);
            setFacts(f.facts ?? []);
            setOutreach(o.outreach ?? []);
            setVersions(v.versions ?? []);
        } catch (e) { console.error('story detail load', e); }
    }, [api, story.id]);
    useEffect(() => { load(); }, [load]);

    const markSent = async (id: string) => {
        try {
            await api.patch(`/api/admin/newsroom/outreach/${id}/status`, { status: 'sent' });
            await load();
        } catch (e) { alert((e as Error).message); }
    };
    const markResponded = async (id: string) => {
        const response = window.prompt('Response text?');
        if (!response) return;
        try {
            await api.patch(`/api/admin/newsroom/outreach/${id}/status`, { status: 'responded', response });
            await load();
        } catch (e) { alert((e as Error).message); }
    };

    return (
        <div onClick={onClose} style={backdropStyle}>
            <div onClick={e => e.stopPropagation()} style={drawerStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                    <h2 style={{ margin: 0, fontFamily: 'Newsreader,serif' }}>{story.headline}</h2>
                    <button type="button" onClick={onClose} style={{ background: 'none', border: 0, fontSize: 20, cursor: 'pointer' }}>×</button>
                </div>
                <div style={sectionStyle}>
                    <h3 style={sectionHead}>Facts ({facts.length})</h3>
                    {facts.length === 0 ? <em style={{ color: '#999' }}>None yet.</em> : facts.map(f => (
                        <div key={f.id} style={{ padding: '6px 0', borderBottom: '1px dotted #eee', fontSize: 13 }}>
                            <span style={{ ...tagStyle(f.confidence), marginRight: 8 }}>{f.confidence}</span>
                            {f.dimension && <span style={{ fontSize: 10, color: '#888', marginRight: 6 }}>[{f.dimension}]</span>}
                            {f.claim}
                            {f.source_url && <> — <a href={f.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>source</a></>}
                            {f.source_quote && <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic', marginTop: 2 }}>"{f.source_quote}"</div>}
                        </div>
                    ))}
                </div>
                <div style={sectionStyle}>
                    <h3 style={sectionHead}>Outreach ({outreach.length})</h3>
                    {outreach.length === 0 ? <em style={{ color: '#999' }}>None queued.</em> : outreach.map(o => (
                        <div key={o.id} style={{ padding: '6px 0', borderBottom: '1px dotted #eee', fontSize: 13 }}>
                            <div><strong>{o.recipient}</strong> · {o.channel} {o.contact && <>· <code style={{ fontSize: 11 }}>{o.contact}</code></>}</div>
                            <div style={{ color: '#333' }}>{o.question}</div>
                            <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                                <span style={{ fontSize: 10, color: '#888' }}>{o.status}</span>
                                {o.status === 'draft' && <button type="button" onClick={() => markSent(o.id)} style={btnSm}>Mark sent</button>}
                                {o.status === 'sent' && <button type="button" onClick={() => markResponded(o.id)} style={btnSm}>Mark responded</button>}
                            </div>
                            {o.response && <div style={{ fontSize: 12, color: '#333', marginTop: 4, background: '#f6f4ea', padding: 6 }}>↳ {o.response}</div>}
                        </div>
                    ))}
                </div>
                <div style={sectionStyle}>
                    <h3 style={sectionHead}>Published versions ({versions.length})</h3>
                    {versions.length === 0 ? <em style={{ color: '#999' }}>Not yet published.</em> : versions.map(v => (
                        <div key={v.id} style={{ padding: 8, marginBottom: 6, border: '1px dotted #ccc' }}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>v{v.version_num}: {v.headline}</div>
                            <div style={{ fontSize: 10, color: '#888' }}>{new Date(v.published_at).toLocaleString()}</div>
                            {v.what_changed && <div style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4, color: '#5a4' }}>Δ {v.what_changed}</div>}
                            <details style={{ marginTop: 6 }}>
                                <summary style={{ fontSize: 11, color: '#666', cursor: 'pointer' }}>Show body</summary>
                                <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 6 }}>{v.body}</pre>
                            </details>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------- Tuning

function TuningPanel() {
    const api = useApi();
    const [agents, setAgents]     = useState<Agent[]>([]);
    const [pickedID, setPickedID] = useState<string>('');

    useEffect(() => {
        api.get<{ agents: RawAgent[] }>('/api/admin/ai-agents').then(r => {
            setAgents((r.agents ?? []).map(a => ({
                id: a.id, name: a.Name, community_id: a.CommunityID,
                community_slug: a.CommunitySlug, role: a.Role ?? 'editor',
                model: a.Model, approve_style: a.ApproveStyle, active: a.Active,
            })));
        }).catch(() => {});
    }, [api]);

    const picked = agents.find(a => a.id === pickedID);
    return (
        <div>
            <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4 }}>Agent</span>
                <select value={pickedID} onChange={e => setPickedID(e.target.value)} style={{ padding: 8, minWidth: 320 }}>
                    <option value="">— pick an agent —</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name} · {a.role}</option>)}
                </select>
            </label>
            {picked && <AgentTuning agent={picked} />}
        </div>
    );
}

function AgentTuning({ agent }: { agent: Agent }) {
    const api = useApi();
    const [settings, setSettings] = useState<Settings | null>(null);
    const [feeds, setFeeds]       = useState<Feed[]>([]);
    const [saving, setSaving]     = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, f] = await Promise.all([
                api.get<Settings>(`/api/admin/newsroom/agents/${agent.id}/settings`),
                api.get<{ feeds: Feed[] }>(`/api/admin/newsroom/agents/${agent.id}/feeds`),
            ]);
            setSettings(s);
            setFeeds(f.feeds ?? []);
        } catch (e) { console.error('tuning load', e); }
    }, [api, agent.id]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            await api.put(`/api/admin/newsroom/agents/${agent.id}/settings`, settings);
        } catch (e) { alert((e as Error).message); }
        setSaving(false);
    };
    const addFeed = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        try {
            await api.post(`/api/admin/newsroom/agents/${agent.id}/feeds`, {
                kind: fd.get('kind'),
                label: fd.get('label'),
                url: fd.get('url'),
                poll_interval_s: Number(fd.get('poll_interval_s')) || 900,
                active: true,
            });
            (e.currentTarget as HTMLFormElement).reset();
            await load();
        } catch (err) { alert((err as Error).message); }
    };

    if (!settings) return <div>Loading…</div>;
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
            <div>
                <h3 style={sectionHead}>Thresholds & personality</h3>
                <NumField label="Min facts to publish" value={settings.min_facts_to_publish} onChange={n => setSettings({ ...settings, min_facts_to_publish: n })} />
                <NumField label="Min distinct sources" value={settings.min_sources_to_publish} onChange={n => setSettings({ ...settings, min_sources_to_publish: n })} />
                <label style={{ display: 'block', margin: '8px 0' }}>
                    <span style={fieldLabel}>Min confidence to publish</span>
                    <select value={settings.min_confidence_to_publish} onChange={e => setSettings({ ...settings, min_confidence_to_publish: e.target.value })} style={inputStyle}>
                        <option value="unconfirmed">unconfirmed</option>
                        <option value="reported">reported</option>
                        <option value="confirmed">confirmed</option>
                    </select>
                </label>
                <label style={{ display: 'block', margin: '8px 0' }}>
                    <input type="checkbox" checked={settings.require_outreach_before_publish} onChange={e => setSettings({ ...settings, require_outreach_before_publish: e.target.checked })} />
                    &nbsp;Require outreach before publish
                </label>
                <NumField label="Max speculation ratio (0..1)" value={settings.max_speculation_ratio} step={0.1} onChange={n => setSettings({ ...settings, max_speculation_ratio: n })} />
                <NumField label="Auto-kill after N days" value={settings.auto_kill_after_days} onChange={n => setSettings({ ...settings, auto_kill_after_days: n })} />
                <label style={{ display: 'block', margin: '8px 0' }}>
                    <span style={fieldLabel}>Personality guide</span>
                    <textarea value={settings.personality} onChange={e => setSettings({ ...settings, personality: e.target.value })} rows={5} style={{ ...inputStyle, minHeight: 100 }} />
                </label>

                <h3 style={{ ...sectionHead, marginTop: 24 }}>Community-relevance triage</h3>
                <p style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
                    Cheap Haiku pass runs BEFORE fact extraction on every incoming feed item.
                    Empty profile = triage disabled (everything passes through). Describe what
                    this community cares about + what to skip.
                </p>
                <label style={{ display: 'block', margin: '8px 0' }}>
                    <span style={fieldLabel}>Relevance profile</span>
                    <textarea
                        value={settings.relevance_profile ?? ''}
                        onChange={e => setSettings({ ...settings, relevance_profile: e.target.value })}
                        rows={6}
                        placeholder="e.g. Vredehoek + Table Mountain area. Care about: mountain fires, crime, water, load-shedding, mountain trails, local businesses. Skip: national politics, celebrity, sport unless local, generic clickbait."
                        style={{ ...inputStyle, minHeight: 120 }}
                    />
                </label>
                <label style={{ display: 'block', margin: '8px 0' }}>
                    <span style={fieldLabel}>Triage model (cheap)</span>
                    <input
                        type="text"
                        value={settings.triage_model ?? ''}
                        onChange={e => setSettings({ ...settings, triage_model: e.target.value })}
                        placeholder="claude-haiku-4-5-20251001"
                        style={inputStyle}
                    />
                </label>
                <NumField label="Triage max tokens" value={settings.triage_max_tokens ?? 120} onChange={n => setSettings({ ...settings, triage_max_tokens: n })} />
                <button type="button" onClick={save} disabled={saving} style={{ ...btnLg, background: 'var(--accent)', color: '#fff' }}>
                    {saving ? 'Saving…' : 'Save settings'}
                </button>
                <p style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                    Changes land within one poll cycle — no restart needed.
                </p>
            </div>
            <div>
                <h3 style={sectionHead}>Feeds ({feeds.length})</h3>
                {feeds.length === 0 && <em style={{ color: '#999' }}>None yet.</em>}
                {feeds.map(f => (
                    <div key={f.id} style={{ padding: 8, marginBottom: 6, border: '1px dotted #ccc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <strong>{f.label}</strong>
                            <span style={{ fontSize: 10, color: f.active ? '#5a4' : '#888' }}>{f.kind} · {f.active ? 'active' : 'paused'}</span>
                        </div>
                        {f.url && <div style={{ fontSize: 11, color: '#666', wordBreak: 'break-all' }}>{f.url}</div>}
                        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                            every {f.poll_interval_s}s · {f.last_polled_at ? `last ${new Date(f.last_polled_at).toLocaleString()}` : 'never polled'}
                        </div>
                        {f.last_error && <div style={{ fontSize: 10, color: '#8b1919' }}>err: {f.last_error}</div>}
                    </div>
                ))}
                <form onSubmit={addFeed} style={{ marginTop: 12, padding: 10, border: '1px dashed #999' }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 2 }}>Add feed</h4>
                    <select name="kind" defaultValue="rss" style={inputStyle}>
                        <option value="rss">RSS/Atom</option>
                        <option value="webhook">Webhook (tips)</option>
                        <option value="html">HTML scrape (todo)</option>
                    </select>
                    <input name="label" required placeholder="Label (e.g. IOL Cape Town)" style={inputStyle} />
                    <input name="url" placeholder="https://…/feed.xml" style={inputStyle} />
                    <input name="poll_interval_s" type="number" min={60} defaultValue={900} placeholder="Poll interval (s)" style={inputStyle} />
                    <button type="submit" style={btnLg}>Add feed</button>
                </form>
            </div>
        </div>
    );
}

function NumField({ label, value, step, onChange }: { label: string; value: number; step?: number; onChange: (n: number) => void }) {
    return (
        <label style={{ display: 'block', margin: '8px 0' }}>
            <span style={fieldLabel}>{label}</span>
            <input type="number" step={step ?? 1} value={value} onChange={e => onChange(Number(e.target.value))} style={inputStyle} />
        </label>
    );
}

// ---------------------------------------------------------------- Tip form

function TipForm() {
    const api = useApi();
    const [agents, setAgents] = useState<Agent[]>([]);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        api.get<{ agents: RawAgent[] }>('/api/admin/ai-agents').then(r => {
            const mapped: Agent[] = (r.agents ?? []).map(a => ({
                id: a.id, name: a.Name, community_id: a.CommunityID,
                community_slug: a.CommunitySlug, role: a.Role ?? 'editor',
                model: a.Model, approve_style: a.ApproveStyle, active: a.Active,
            }));
            setAgents(mapped.filter(a => a.role === 'reporter'));
        }).catch(() => {});
    }, [api]);

    const submit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        try {
            const story = await api.post<Story>('/api/admin/newsroom/tips', {
                agent_id:    fd.get('agent_id'),
                url:         fd.get('url'),
                headline:    fd.get('headline'),
                note:        fd.get('note'),
                source_kind: fd.get('source_kind'),
            });
            setMsg(`Story created: ${story.headline} (${story.status})`);
            (e.currentTarget as HTMLFormElement).reset();
        } catch (err) {
            setMsg('Failed: ' + (err as Error).message);
        }
    };
    return (
        <form onSubmit={submit} style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: '#666', fontSize: 13 }}>
                Drop a URL or a note and a Reporter Scout will start digging on its next tick.
            </p>
            <label>
                <span style={fieldLabel}>Reporter</span>
                <select name="agent_id" required style={inputStyle}>
                    <option value="">— pick a reporter —</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
            </label>
            <label>
                <span style={fieldLabel}>URL (news article, post, official page)</span>
                <input name="url" type="url" placeholder="https://…" style={inputStyle} />
            </label>
            <label>
                <span style={fieldLabel}>Working headline (optional)</span>
                <input name="headline" style={inputStyle} />
            </label>
            <label>
                <span style={fieldLabel}>Why this looks newsworthy</span>
                <textarea name="note" rows={4} style={{ ...inputStyle, minHeight: 90 }} />
            </label>
            <label>
                <span style={fieldLabel}>Source kind</span>
                <select name="source_kind" defaultValue="news" style={inputStyle}>
                    <option value="news">News outlet</option>
                    <option value="official">Official (govt/council/police)</option>
                    <option value="social">Social media</option>
                    <option value="witness">Witness account</option>
                    <option value="record">Public record</option>
                </select>
            </label>
            <button type="submit" style={{ ...btnLg, background: 'var(--accent)', color: '#fff' }}>Submit tip</button>
            {msg && <div style={{ fontSize: 13, color: msg.startsWith('Failed') ? '#8b1919' : '#2b5e3f' }}>{msg}</div>}
        </form>
    );
}

// ---------------------------------------------------------------- styles

const colStyle: React.CSSProperties = { background: '#faf8f2', border: '1px solid #ccc', padding: 8, minHeight: 200 };
const colHead:  React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 8, borderBottom: '1px solid #ccc', paddingBottom: 4 };
const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #ccc', padding: 8, marginBottom: 6, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const cardTitle: React.CSSProperties = { fontFamily: 'Newsreader,serif', fontSize: 13, fontWeight: 700, color: '#111' };
const moveBtn:  React.CSSProperties = { fontSize: 10, background: 'none', border: '1px solid #aaa', padding: '1px 5px', cursor: 'pointer', borderRadius: 2 };
const backdropStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' };
const drawerStyle: React.CSSProperties = { width: 560, maxWidth: '100vw', background: '#fdfcf6', padding: 20, overflowY: 'auto', boxShadow: '-4px 0 12px rgba(0,0,0,0.2)' };
const sectionStyle: React.CSSProperties = { marginBottom: 20 };
const sectionHead: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, marginBottom: 6, borderBottom: '1px solid #ccc', paddingBottom: 3 };
const fieldLabel: React.CSSProperties = { fontSize: 11, color: '#666', display: 'block', marginBottom: 3 };
const inputStyle: React.CSSProperties = { width: '100%', padding: 6, border: '1px solid #999', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' };
const btnSm:  React.CSSProperties = { fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid #aaa', background: '#fff' };
const btnLg:  React.CSSProperties = { padding: '8px 16px', cursor: 'pointer', border: 0, fontFamily: '-apple-system,sans-serif', textTransform: 'uppercase', letterSpacing: 1, fontSize: 12 };

const adminNav: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLink: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};

function tagStyle(c: Confidence): React.CSSProperties {
    const bg: Record<Confidence, string> = {
        confirmed:    '#c6e6c9',
        reported:     '#f2eecb',
        unconfirmed:  '#f2dfc3',
        contradicted: '#f2c9c9',
        gap:          '#e0e0e0',
        unknown:      '#e0e0e0',
    };
    return {
        display: 'inline-block', padding: '1px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
        background: bg[c] ?? '#eee', color: '#333',
    };
}
